import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { InputSwitch } from 'primereact/inputswitch';
import * as echarts from 'echarts';

export const ApacheEchart = React.memo(forwardRef(({ chartOpciones, showToolbar = false, isLoading = false }, ref) => {
    const chartContainerRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [darkMode, setDarkMode] = useState(false);
    const [useDecal, setUseDecal] = useState(false);
    const [renderer, setRenderer] = useState('canvas');
    const [isReady, setIsReady] = useState(false);

    const MAPS = {
        Ecuador: './ecuador.geojson',
        CantonesEcuador: './cantones-ecuador.geojson',
        // Más mapas en el futuro
        // Colombia: '/colombia.geojson',
    };

    // Función para agregar/quitar decal
    const agregarPatronRelleno = useCallback((options, applyDecal) => {
        if (!options?.series) return options;
        return {
            ...options,
            series: options.series.map((serie) => {
                if (applyDecal) {
                    return {
                        ...serie,
                        itemStyle: {
                            ...serie.itemStyle,
                            decal: {
                                symbol: 'line',
                                symbolSize: 2,
                                rotation: Math.PI / 4,
                                color: 'rgba(0,0,0,0.2)',
                            },
                        },
                    };
                } else {
                    const { decal, ...restItemStyle } = serie.itemStyle || {};
                    return { ...serie, itemStyle: restItemStyle };
                }
            })
        };
    }, []);

    // Helper para mover polígonos (sea Polygon o MultiPolygon)
    const moverCoordenadasX = (coords, desplazamientoX) => {
        // Determinamos la profundidad del arreglo para saber si es MultiPolygon o Polygon
        if (typeof coords[0][0] === 'number') {
            return coords.map(([lng, lat]) => [lng + desplazamientoX, lat]);
        }
        return coords.map(ring => moverCoordenadasX(ring, desplazamientoX));
    };

    // Ajuste de Galápagos unificado
    const ajustarGeojson = (geoJson, mapName) => {
        if (!geoJson?.features) return geoJson;

        const needsAdjustment = (f) =>
            (mapName === 'Ecuador' && f.properties?.ISO === 'EC-W') ||
            (mapName === 'CantonesEcuador' && f.properties?.DPA_DESPRO === 'GALAPAGOS');

        return {
            ...geoJson,
            features: geoJson.features.map(f => {
                if (needsAdjustment(f)) {
                    return {
                        ...f,
                        geometry: {
                            ...f.geometry,
                            coordinates: moverCoordenadasX(f.geometry.coordinates, 6)
                        }
                    };
                }
                return f;
            }),
        };
    };

    const onChartClick = (params) => {
        // console.log(params);
    };

    // INICIALIZAR o DESTRUIR el gráfico (Solo cuando cambia el motor o el tema)
    useEffect(() => {
        if (!chartContainerRef.current) return;
        // Si ya hay instancia, la destruimos porque cambió renderer o theme
        if (chartInstanceRef.current) {
            chartInstanceRef.current.dispose();
        }
        // Crear nueva instancia
        chartInstanceRef.current = echarts.init(
            chartContainerRef.current,
            darkMode ? 'dark' : null,
            { renderer, locale: 'es' }
        );
        chartInstanceRef.current.on('click', onChartClick);
        // Al cambiar de tema, debemos re-aplicar las opciones si ya existían
        if (chartOpciones && isReady) {
            const options = agregarPatronRelleno(chartOpciones, useDecal);
            // notMerge: true limpia el canvas antes de dibujar el nuevo tema
            chartInstanceRef.current.setOption(options, { notMerge: true });
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.dispose();
                chartInstanceRef.current = null;
            }
        };
    }, [darkMode, renderer]); // Solo se ejecuta al montar o cambiar configs visuales base

    // ACTUALIZAR DATOS y CARGAR MAPAS (Se ejecuta cuando cambian las opciones)
    useEffect(() => {
        if (!chartInstanceRef.current || !chartOpciones || (!chartOpciones.series && !chartOpciones.graphic)) return;

        const mapSeries = chartOpciones.series?.find(s => s.type === 'map');
        const mapName = mapSeries?.map;

        const updateChart = () => {
            const finalOptions = agregarPatronRelleno(chartOpciones, useDecal);
            // notMerge: false (por defecto) permite que ECharts interpole y anime los cambios de datos
            chartInstanceRef.current.setOption(finalOptions, { notMerge: true, replaceMerge: ['series'] });
            setIsReady(true);
        };

        if (mapSeries && mapName && MAPS[mapName] && !echarts.getMap(mapName)) {
            // Si hay mapa, no está cargado y existe en nuestra lista
            setIsReady(false);
            fetch(MAPS[mapName]).then(res => res.json()).then(geoJson => {
                const adjustedGeoJson = ajustarGeojson(geoJson, mapName);
                echarts.registerMap(mapName, adjustedGeoJson);
                updateChart();
            }).catch(error => {
                updateChart(); // Renderizar de todos modos sin mapa
            });
        } else {
            // No requiere mapa, o ya está registrado (echarts.getMap devolvió algo)
            updateChart();
        }
    }, [chartOpciones, useDecal]); // Separado del init

    // Hook de Loading
    useEffect(() => {
        if (chartInstanceRef.current) {
            if (isLoading && !isReady) {
                chartInstanceRef.current.showLoading('default', {
                    text: 'Cargando mapa...',
                    color: '#3B82F6',
                    textColor: darkMode ? '#ffffff' : '#4b5563',
                    maskColor: darkMode ? 'rgba(16, 12, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                    zlevel: 0
                });
            } else if (isLoading) {
                chartInstanceRef.current.showLoading('default', {
                    text: 'Actualizando...',
                    color: '#3B82F6',
                    textColor: darkMode ? '#ffffff' : '#4b5563',
                    maskColor: darkMode ? 'rgba(16, 12, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                    zlevel: 0
                });
            } else {
                chartInstanceRef.current.hideLoading();
            }
        }
    }, [isLoading, darkMode, isReady]);

    // Hook para Resize
    useEffect(() => {
        if (!isReady || !chartInstanceRef.current || !chartContainerRef.current) return;
        let timeoutId;
        const resizeObserver = new ResizeObserver(() => {
            clearTimeout(timeoutId);// Limpiamos el timeout anterior si el usuario sigue redimensionando
            // Esperamos 100ms después del último evento de resize para redibujar
            timeoutId = setTimeout(() => {
                chartInstanceRef.current?.resize();
            }, 100);
        });
        resizeObserver.observe(chartContainerRef.current);
        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [isReady]);

    const toolBarStartContent = () => {
        const isKPI = !!chartOpciones?.graphic && !chartOpciones?.series;
        // Uso un color base que herede del padre ('inherit') en lugar de '#333' para evitar problemas en temas oscuros globales
        const textColor = darkMode ? '#ffffff' : 'inherit';

        return (
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 'bold', color: renderer === 'svg' ? '#0d6efd' : textColor }}>
                        Usar SVG
                    </span>
                    <InputSwitch checked={renderer === 'svg'} onChange={() => setRenderer(renderer === 'canvas' ? 'svg' : 'canvas')} />
                </div>
                {!isKPI && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: 'bold', color: darkMode ? '#0d6efd' : textColor }}>
                                Modo oscuro
                            </span>
                            <InputSwitch checked={darkMode} onChange={() => setDarkMode(!darkMode)} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: 'bold', color: useDecal ? '#0d6efd' : textColor }}>
                                Patrón de relleno
                            </span>
                            <InputSwitch checked={useDecal} onChange={() => setUseDecal(!useDecal)} />
                        </div>
                    </>
                )}
            </div>
        );
    };

    useImperativeHandle(ref, () => ({
        captureImage: (options = {}) => {
            if (!chartInstanceRef.current) return null;
            const instance = chartInstanceRef.current;
            const bgColor = options.backgroundColor || (darkMode ? '#100C2A' : '#ffffff');
            //  Guardar dimensiones actuales de la pantalla del usuario
            const originalWidth = instance.getWidth();
            const originalHeight = instance.getHeight();
            //  Si nos pasan un tamaño objetivo para el PDF, redimensionamos temporalmente
            if (options.targetWidth && options.targetHeight) {
                instance.resize({ width: options.targetWidth, height: options.targetHeight });
            }
            const defaultOptions = {
                type: 'png',
                pixelRatio: 2,
                excludeComponents: ['toolbox'],
                backgroundColor: bgColor
            };
            // Tomamos la "foto" con el tamaño perfecto
            const imgBase64 = instance.getDataURL({ ...defaultOptions, ...options });
            // Restauramos el gráfico a como estaba en la pantalla del usuario
            if (options.targetWidth && options.targetHeight) {
                instance.resize({ width: originalWidth, height: originalHeight });
            }

            return imgBase64;
        },
        getInstance: () => chartInstanceRef.current
    }));

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            {showToolbar && (
                <Toolbar style={{ width: '100%', marginBottom: '10px', flexShrink: 0, background: 'transparent', border: 'none', borderBottom: '1px solid #dee2e6', boxShadow: 'none', padding: '0.8rem 0.5rem', }} start={toolBarStartContent()} />
            )}
            <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0, minWidth: 0 }}>
                <div ref={chartContainerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, touchAction: 'pan-y' }} />
            </div>
        </div>
    );
}));